import React, { Dispatch, SetStateAction, useEffect } from 'react'

import { FormikProps, FormikValues } from 'formik'

import { CommonSteps } from './CommonSteps'
import { DirectoryModeStep } from './DirectoryModeStep'
import { NonApplicantForm } from './NonApplicantForm'
import { DirectoryOption, MarriedOption, PartyOption } from './index'
import Stepper, { Step } from '@/components/Stepper'
import { ProfileTypes } from '@/constants/applicationProfile'
import { ContactForm } from '@/features/FileContacts/AddContactDrawer/AddContactDrawerStepper/ContactForm'
import { ApplicationProfile, Company } from '@/types/unlock'

interface AddContactDrawerStepsProps {
  selectedType: string
  setSelectedType: Dispatch<SetStateAction<string>>
  selectedCategory: string
  applicationExternalId: string
  companies: Company[]
  marriedOptions: MarriedOption[]
  directoryOptions: DirectoryOption[]
  partyOptions: PartyOption[]
  isError: boolean
  errorMessage: string
  formikProps: FormikProps<FormikValues>
  applicationProfiles: ApplicationProfile[]
}

export const DrawerStepper = ({
  selectedType,
  setSelectedType,
  selectedCategory,
  applicationExternalId,
  applicationProfiles,
  companies,
  marriedOptions,
  partyOptions,
  directoryOptions,
  isError,
  errorMessage,
  formikProps,
}: AddContactDrawerStepsProps) => {
  const { values, setValues, handleSubmit, isSubmitting, isValid, resetForm } = formikProps

  const preFillFormValues = async () => {
    let newValues: FormikValues = {
      currentMode: values.currentMode,
      contact_type: values.contact_type,
      manual_name: '',
      contact_name: '',
      phone: '',
      email: '',
      address: '',
      title: '',
    }

    const placeholderContact = companies.find(
      (company) => company.type === selectedType && company.is_default_placeholder
    )

    if (placeholderContact) {
      const { uuid, name, phone, email, address, contact_name } = placeholderContact
      newValues = {
        ...newValues,
        directory_name: uuid,
        manual_name: name || '',
        contact_name: contact_name || '',
        phone: phone || '',
        email: email || '',
        address: address || '',
      }
    }

    resetForm({ values: newValues })
  }

  useEffect(() => {
    /**
     * Clean up form fields when switching types on Manual mode.
     * If the type has a Company with the `is_default_placeholder` flag,
     * it will prefill the form with the placeholder data
     */
    if (values.currentMode === 'new') {
      preFillFormValues()
    }
  }, [selectedType, values.currentMode])

  const getSteps = () => {
    const steps: Step[] = CommonSteps({ values, partyOptions, setSelectedType, setValues })
    if (values.currentMode == 'new') {
      if ([ProfileTypes.NOO, ProfileTypes.CO_OWNER].includes(values.contact_type)) {
        steps.push(
          NonApplicantForm({
            values,
            marriedOptions,
            applicationExternalId,
            formikProps,
            applicationProfiles,
          })
        )
      } else {
        steps.push(ContactForm({ values, selectedCategory }))
      }
    } else if (values.currentMode == 'directory') {
      steps.push(DirectoryModeStep({ values, selectedType, directoryOptions, companies }))
    }
    return steps
  }

  return (
    <Stepper
      steps={getSteps()}
      finishButton={{
        label: isError ? 'Retry - Add Contact' : 'Finish - Add Contact',
        onClick: () => handleSubmit(),
        extraDisabled: isSubmitting || !isValid,
        footerMessage: errorMessage && (
          <div className={`${isError && 'text-red-500'}`}>{errorMessage}</div>
        ),
        loadingFooter: isSubmitting,
      }}
    />
  )
}
